/**
 * GA4 dataLayer tracker — custom GTM events for secom.ro.
 *
 * Port of the old theme's snippets/GA4_dataLayer.liquid (SPG_DataLayer) restructured for Prestige:
 * - page configuration comes from window.SPG_DataLayerConfig (snippets/GA4_dataLayer.liquid)
 * - product data is read from product cards ([data-product-object], snippets/GA4_dataLayer_product.liquid)
 * - cart mutations are detected by wrapping fetch / XMLHttpRequest (app-agnostic, same as the old theme)
 * - prices are synced with Discount Ninja (la:dn:* events) before the events are pushed
 *
 * Contracts (event names, item schema, sessionStorage keys) are documented in docs/GTM-DATALAYER-README.md.
 * The pure helpers are unit-tested with `node --test tests/spg-datalayer.test.js`.
 */
(function (globalScope) {
  "use strict";

  const DEFAULT_OPTIONS = {
    waitTime: 2000, // delay before view_item / select_item / view_item_list / add_to_cart / remove_from_cart (lets Discount Ninja finish)
    viewportThreshold: 0.6, // a card counts as an impression when at least 60 % visible
    impressionDebounce: 700, // batch window for view_item_list
    mutationDebounce: 150, // re-scan delay after DOM changes (facets, recommendations, drawer re-render)
    searchResultsCount: 5, // items sent with view_search_result
    discountNinjaWait: 4000, // max wait for la:dn:cart:updated before view_cart is pushed from the drawer
  };

  const STORAGE_KEYS = {
    listNameMap: "itemListMap", // handle → item_list_name (read by the custom web pixel at purchase)
    listIdMap: "itemIdListMap", // handle → item_list_id (read by the custom web pixel at purchase)
    bundleMap: "sec_cartBundleMap", // bundle parent product id → cart item with item_components (read by the custom web pixel)
    productBreadcrumb: "productBreadcrumb", // last clicked card: { handle, item_list_name, item_list_id }
    selectedProduct: "selectedProduct", // pending select_item: { handle, item_list_name, item_list_id, index }
  };

  const SELECTORS = {
    productCard: "[data-product-object]",
    productLink: "a[href*=\"/products/\"]",
    sectionRoot: ".shopify-section",
    sectionHeading: ".section-header .h2, .section-header h2",
    sectionFallbackHeading: "h2",
    listNameOverride: "[data-spg-list-name]",
    collectionIdHolder: "[data-collection-id]",
    swiperSlide: "[data-swiper-slide-index]",
    cartDrawer: "#cart-drawer",
    cartChangeLink: "a[href*=\"/cart/change\"]",
    cartQuantityInput: "input[name=\"updates[]\"][data-line-key]",
    sortSelect: "select[name=\"sort_by\"]",
    facetInput: "facets-form input[name^=\"filter.\"]",
    tagFilterItem: "li[data-tag][data-group]",
  };

  // Window events dispatched after an intercepted Shopify cart request (same names as the old theme).
  const CART_EVENTS = {
    add: "SCE:add",
    update: "SCE:update",
    change: "SCE:change",
    clear: "SCE:clear",
    get: "SCE:get",
  };

  const PAGE_CHANGE_DEDUPE_WINDOW = 15000; // ms: a cart-page link push suppresses the matching AJAX change event

  // ------------------------------------------------------------------------------------------------------------------
  // Pure helpers (unit-tested)
  // ------------------------------------------------------------------------------------------------------------------

  const roundMoney = (amount) => +(Number(amount) || 0).toFixed(2);

  const toMoney = (cents) => roundMoney((Number(cents) || 0) / 100);

  const cartRequestType = (url) => {
    if (typeof url !== "string" || !url) return null;
    const path = url.split("?")[0].split("#")[0];
    if (/\/cart\.js$/.test(path)) return "get";
    const match = path.match(/\/cart\/(add|update|change|clear)(\.js)?$/);
    return match ? match[1] : null;
  };

  const readHeader = (headers, name) => {
    if (!headers) return "";
    if (typeof headers.get === "function") return headers.get(name) || "";
    const matchingKey = Object.keys(headers).find((candidate) => candidate.toLowerCase() === name.toLowerCase());
    return matchingKey ? headers[matchingKey] : "";
  };

  const parseRequestBody = (body, headers) => {
    if (!body) return {};
    try {
      if (typeof FormData !== "undefined" && body instanceof FormData) return Object.fromEntries(body.entries());
      if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) return Object.fromEntries(body.entries());
      if (typeof body === "string") {
        const contentType = readHeader(headers, "Content-Type");
        const trimmedBody = body.trim();
        if (contentType.includes("application/x-www-form-urlencoded")) return Object.fromEntries(new URLSearchParams(trimmedBody).entries());
        if (trimmedBody.startsWith("{") || trimmedBody.startsWith("[")) return JSON.parse(trimmedBody);
        if (trimmedBody.includes("=")) return Object.fromEntries(new URLSearchParams(trimmedBody).entries());
        return {};
      }
      if (typeof body === "object") return body;
    } catch (error) {
      /* unparsable body */
    }
    return {};
  };

  const formatProductData = (productJson, options = {}) => {
    const variants = Array.isArray(productJson.variants) ? productJson.variants : [];
    const requestedVariantId = options.variantId != null ? String(options.variantId) : null;
    const variant = variants.find((candidate) => String(candidate.id) === requestedVariantId) || variants[0] || {};
    const price = toMoney(variant.price);
    const compareAtPrice = variant.compare_at_price ? toMoney(variant.compare_at_price) : price;
    const discount = compareAtPrice > price ? roundMoney(compareAtPrice - price) : 0;
    const firstImage = Array.isArray(productJson.images) && productJson.images.length ? productJson.images[0] : productJson.featured_image;
    return {
      item_id: productJson.id,
      product_id: productJson.id,
      item_name: productJson.title || "",
      item_brand: productJson.vendor || "",
      currency: options.currency || "",
      item_category: productJson.product_type || "",
      item_variant: productJson.product_type || "",
      price,
      compare_at_price: discount > 0 ? compareAtPrice : price,
      discount,
      sale_price: price,
      quantity: 1,
      handle: productJson.handle || "",
      sku: variant.sku || "",
      stock: variant.available === false ? "out of stock" : "in stock",
      link: (options.shopUrl || "") + "/products/" + (productJson.handle || ""),
      variant: typeof firstImage === "string" ? firstImage : (firstImage && firstImage.src) || "",
      variantId: variant.id,
      variantTitle: variant.title || "",
      item_list_name: options.listName || "direct",
      item_list_id: options.listId != null ? options.listId : "",
    };
  };

  const buildCartItems = (cartJson, options = {}) => {
    const lines = cartJson && Array.isArray(cartJson.items) ? cartJson.items : [];
    const currency = (cartJson && cartJson.currency) || options.currency || "";
    const resolveListName = options.resolveListName || (() => "direct");
    const resolveListId = options.resolveListId || (() => "");
    return lines.map((line) => {
      const price = toMoney(line.final_price);
      const originalPrice = line.original_price != null ? toMoney(line.original_price) : price;
      const discount = originalPrice > price ? roundMoney(originalPrice - price) : 0;
      return {
        item_id: line.product_id,
        product_id: line.product_id,
        item_name: line.product_title || line.title || "",
        item_brand: line.vendor || "",
        currency,
        item_category: line.product_type || "",
        item_variant: line.product_type || "",
        price,
        sale_price: price,
        original_price: originalPrice,
        compare_at_price: discount > 0 ? originalPrice : price,
        discount,
        line_price: toMoney(line.final_line_price != null ? line.final_line_price : line.line_price),
        original_line_price: toMoney(line.original_line_price != null ? line.original_line_price : line.line_price),
        line_level_total_discount: toMoney(line.line_level_total_discount),
        quantity: line.quantity,
        handle: line.handle || "",
        sku: line.sku || "",
        link: (options.shopUrl || "") + (line.url || ""),
        variant: line.image || "",
        variantId: line.variant_id,
        variantTitle: line.variant_title || "",
        item_list_name: resolveListName(line.handle),
        item_list_id: resolveListId(line.handle),
        item_components:
          Array.isArray(line.item_components) && line.item_components.length ? buildCartItems({ items: line.item_components, currency }, options) : [],
      };
    });
  };

  const readMap = (storage, key) => {
    try {
      const raw = storage.getItem(key);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (error) {
      return {};
    }
  };

  const writeMapEntry = (storage, key, handle, value) => {
    if (!handle) return;
    try {
      const map = readMap(storage, key);
      map[handle] = value;
      storage.setItem(key, JSON.stringify(map));
    } catch (error) {
      /* storage blocked */
    }
  };

  const readMapEntry = (storage, key, handle, fallback) => {
    const map = readMap(storage, key);
    return handle && map[handle] != null && map[handle] !== "" ? map[handle] : fallback;
  };

  const ecommerceEvents = (eventName, ecommerce) => [{ ecommerce: null }, { event: eventName, ecommerce }];

  const normalizeListName = (text) => String(text || "").replace(/\s+/g, " ").trim();

  const parseProductObject = (raw) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const sumItemsValue = (items) =>
    roundMoney((items || []).reduce((total, item) => total + (Number(item.price) || 0) * (Number(item.quantity) || 0), 0));

  // /cart/add.js answers with one line item, or { items: [...] } when several were posted.
  const addedLineItems = (responseJson) => {
    if (!responseJson || typeof responseJson !== "object") return [];
    if (Array.isArray(responseJson.items)) return responseJson.items;
    return [responseJson];
  };

  // Shopify returns the line's TOTAL quantity, so the added amount is what the request asked for.
  const addedQuantity = (requestBody, lineItem) => {
    const body = requestBody || {};
    const variantId = lineItem && lineItem.variant_id != null ? String(lineItem.variant_id) : null;
    let requested;
    if (Array.isArray(body.items)) {
      const matchingEntry = body.items.find((entry) => String(entry.id) === variantId) || body.items[0];
      requested = matchingEntry && matchingEntry.quantity;
    } else if (body.quantity != null) {
      requested = body.quantity;
    }
    const quantity = parseInt(requested, 10);
    return quantity > 0 ? quantity : 1;
  };

  // items_added / items_removed of a /cart/change.js response carry the changed quantity per line.
  const computeCartChange = (changedLines, knownItemsByVariant, options = {}) => {
    const items = (changedLines || []).map((line) => {
      const knownItem = knownItemsByVariant && knownItemsByVariant[line.variant_id];
      const quantity = Number(line.quantity) || 1;
      if (knownItem) {
        return Object.assign({}, knownItem, { quantity, price: line.final_price != null ? toMoney(line.final_price) : knownItem.price });
      }
      return Object.assign(buildCartItems({ items: [line], currency: options.currency }, options)[0], { quantity });
    });
    return { items, value: sumItemsValue(items) };
  };

  // Added / removed items derived from the difference between the known cart snapshot and a cart response
  // (quantities summed per variant). Used when a change response carries no items_added / items_removed.
  const cartDelta = (previousItems, cartJson, options = {}) => {
    const lines = cartJson && Array.isArray(cartJson.items) ? cartJson.items : [];
    const previousByVariant = {};
    (previousItems || []).forEach((item) => {
      const key = String(item.variantId);
      previousByVariant[key] = previousByVariant[key] || { item, quantity: 0 };
      previousByVariant[key].quantity += Number(item.quantity) || 0;
    });
    const nextByVariant = {};
    lines.forEach((line) => {
      const key = String(line.variant_id);
      nextByVariant[key] = nextByVariant[key] || { line, quantity: 0 };
      nextByVariant[key].quantity += Number(line.quantity) || 0;
    });
    const added = [];
    const removed = [];
    Object.keys(nextByVariant).forEach((key) => {
      const next = nextByVariant[key];
      const previous = previousByVariant[key];
      const difference = next.quantity - (previous ? previous.quantity : 0);
      if (difference > 0) {
        added.push(
          previous
            ? Object.assign({}, previous.item, { quantity: difference, price: next.line.final_price != null ? toMoney(next.line.final_price) : previous.item.price })
            : Object.assign(buildCartItems({ items: [next.line], currency: options.currency }, options)[0], { quantity: difference })
        );
      } else if (difference < 0) {
        removed.push(Object.assign({}, previous.item, { quantity: -difference }));
      }
    });
    Object.keys(previousByVariant).forEach((key) => {
      if (!nextByVariant[key]) removed.push(Object.assign({}, previousByVariant[key].item, { quantity: previousByVariant[key].quantity }));
    });
    return { added, removed };
  };

  // discountNinja.api.discountedCart.content.items → price overrides by variant id (cents → amounts).
  const mergeDiscountNinjaPrices = (items, ninjaItems) => {
    const list = items || [];
    if (!Array.isArray(ninjaItems) || !ninjaItems.length) return list;
    const overridesByVariant = {};
    ninjaItems.forEach((entry) => {
      const variantId = entry && entry.product && entry.product.variantId;
      if (variantId == null) return;
      const productPrice = (entry.price && entry.price.product) || {};
      const discountAmountCents = (entry.discount && entry.discount.product && entry.discount.product.amount) || 0;
      overridesByVariant[String(variantId)] = {
        compare_at_price: toMoney(productPrice.compareAt),
        price: toMoney(productPrice.discounted),
        discount: toMoney(discountAmountCents),
        original_price: toMoney(productPrice.original),
      };
    });
    return list.map((item) => {
      const override = overridesByVariant[String(item.variantId)];
      return override ? Object.assign({}, item, override) : item;
    });
  };

  const buildBundleMap = (cartItems) => {
    const bundleMap = {};
    (cartItems || []).forEach((item) => {
      if (Array.isArray(item.item_components) && item.item_components.length) bundleMap[item.item_id] = Object.assign({}, item);
    });
    return bundleMap;
  };

  // Re-prices the saved bundles from the Discount Ninja cart (la:dn:cart:updated), as the old updateBundlePrices did.
  const updateBundleMap = (bundleMap, ninjaCart) => {
    const updated = {};
    Object.keys(bundleMap || {}).forEach((key) => {
      updated[key] = Object.assign({}, bundleMap[key]);
    });
    const ninjaItems = ninjaCart && Array.isArray(ninjaCart.items) ? ninjaCart.items : [];
    ninjaItems.forEach((ninjaItem) => {
      const productId = ninjaItem && ninjaItem.product && ninjaItem.product.productId;
      const bundle = productId != null ? updated[String(productId)] : null;
      if (!bundle) return;
      const productDiscount = (ninjaItem.discount && ninjaItem.discount.product) || {};
      const discountPercentage = Number(productDiscount.percentage) || 0;
      const discountAmount = toMoney(productDiscount.amount);
      const quantity = Number(ninjaItem.quantity) || Number(bundle.quantity) || 1;
      const compareAtPrice = Number(bundle.compare_at_price) || 0;
      const salePrice = roundMoney(compareAtPrice - discountAmount);
      Object.assign(bundle, {
        discount: discountAmount,
        line_level_total_discount: roundMoney(discountAmount * quantity),
        line_price: roundMoney(salePrice * quantity),
        original_line_price: roundMoney(compareAtPrice * quantity),
        price: salePrice,
        sale_price: salePrice,
        quantity,
        item_components: (bundle.item_components || []).map((component) => {
          const originalPrice = Number(component.original_price) || 0;
          const componentSalePrice = roundMoney((originalPrice * (100 - discountPercentage)) / 100);
          const componentDiscount = roundMoney(originalPrice - componentSalePrice);
          return Object.assign({}, component, {
            line_level_total_discount: roundMoney(componentDiscount * quantity),
            line_price: roundMoney(componentSalePrice * quantity),
            original_line_price: roundMoney(originalPrice * quantity),
            price: componentSalePrice,
            quantity,
            sale_price: componentSalePrice,
          });
        }),
      });
    });
    return updated;
  };

  // Cart page quantity controls navigate to /cart/change?id=<line key>&quantity=<target> (no AJAX event).
  // Returns { lineKey, quantity } for ?id=<variantId:hash> links or { line, quantity } (1-based) for ?line=N links.
  const parseCartChangeLink = (href) => {
    if (typeof href !== "string" || !href.includes("/cart/change")) return null;
    const queryString = href.split("?")[1];
    if (!queryString) return null;
    const params = new URLSearchParams(queryString.split("#")[0]);
    const quantity = params.get("quantity");
    if (quantity == null || quantity === "" || Number.isNaN(Number(quantity))) return null;
    const lineKey = params.get("id");
    if (lineKey) return { lineKey, quantity: Number(quantity) };
    const line = parseInt(params.get("line"), 10);
    if (line > 0) return { line, quantity: Number(quantity) };
    return null;
  };

  // `reference` is a line key (variantId:hash) or a 1-based line number in cart order.
  const cartPageChange = (reference, targetQuantity, cartItems) => {
    const snapshotItems = cartItems || [];
    let item = null;
    if (typeof reference === "number") {
      item = snapshotItems[reference - 1] || null;
    } else {
      const variantId = String(reference || "").split(":")[0];
      item = snapshotItems.find((candidate) => String(candidate.variantId) === variantId) || null;
    }
    if (!item) return null;
    const delta = Number(targetQuantity) - (Number(item.quantity) || 0);
    if (!delta || Number.isNaN(delta)) return null;
    const items = [Object.assign({}, item, { quantity: Math.abs(delta) })];
    return { action: delta > 0 ? "add" : "remove", items, value: sumItemsValue(items) };
  };

  // Discount Ninja's discounted cart: la:dn:cart:updated detail.data[0], or discountNinja.api.discountedCart.content.
  const discountedCartFromEvent = (event) => {
    const data = event && event.detail && event.detail.data;
    return Array.isArray(data) && data[0] && typeof data[0] === "object" ? data[0] : null;
  };

  // True when the Discount Ninja cart describes exactly the Shopify cart (same variants, same quantities).
  const discountedCartMatchesCart = (discountedCart, cartJson) => {
    if (!discountedCart || !Array.isArray(discountedCart.items)) return false;
    const cartLines = cartJson && Array.isArray(cartJson.items) ? cartJson.items : [];
    const ninjaQuantities = {};
    discountedCart.items.forEach((entry) => {
      const variantId = entry && entry.product && entry.product.variantId;
      if (variantId == null) return;
      ninjaQuantities[String(variantId)] = (ninjaQuantities[String(variantId)] || 0) + (Number(entry.quantity) || 0);
    });
    const cartQuantities = {};
    cartLines.forEach((line) => {
      cartQuantities[String(line.variant_id)] = (cartQuantities[String(line.variant_id)] || 0) + (Number(line.quantity) || 0);
    });
    const cartVariants = Object.keys(cartQuantities);
    if (Object.keys(ninjaQuantities).length !== cartVariants.length) return false;
    return cartVariants.every((variantId) => ninjaQuantities[variantId] === cartQuantities[variantId]);
  };

  // view_cart payload: items merged with the Discount Ninja prices, value = DN discounted subtotal (or the cart total).
  const viewCartPayload = ({ items, discountedCart, currency, fallbackValue }) => {
    const ninjaItems = discountedCart && Array.isArray(discountedCart.items) ? discountedCart.items : null;
    const mergedItems = ninjaItems ? mergeDiscountNinjaPrices(items || [], ninjaItems) : items || [];
    let value = roundMoney(fallbackValue);
    if (ninjaItems) {
      const discountedSubtotal = discountedCart.subtotal && discountedCart.subtotal.discountedPrice;
      value = discountedSubtotal != null ? toMoney(discountedSubtotal) : sumItemsValue(mergedItems);
    }
    return { currency, value, items: mergedItems };
  };

  // item_list_name of a card: explicit override → the current collection's title for its own grid (the section
  // heading there can be the SEO title_tag) → the section heading → the page default.
  const listNameFromContext = (context, config) => {
    const overrideName = normalizeListName(context && context.overrideName);
    if (overrideName) return overrideName;
    const holderCollectionId = context && context.holderCollectionId;
    const onOwnCollectionGrid =
      config.templateName === "collection" && config.collectionId != null && holderCollectionId != null && String(holderCollectionId) === String(config.collectionId);
    if (onOwnCollectionGrid && config.collectionTitle) return config.collectionTitle;
    const headingText = normalizeListName(context && context.headingText);
    if (headingText) return headingText;
    return config.itemListName || "direct";
  };

  const helpers = {
    roundMoney,
    toMoney,
    cartRequestType,
    parseRequestBody,
    formatProductData,
    buildCartItems,
    readMap,
    writeMapEntry,
    readMapEntry,
    ecommerceEvents,
    normalizeListName,
    parseProductObject,
    sumItemsValue,
    addedLineItems,
    addedQuantity,
    computeCartChange,
    cartDelta,
    mergeDiscountNinjaPrices,
    buildBundleMap,
    updateBundleMap,
    parseCartChangeLink,
    cartPageChange,
    listNameFromContext,
    discountedCartFromEvent,
    discountedCartMatchesCart,
    viewCartPayload,
  };

  // ------------------------------------------------------------------------------------------------------------------
  // Browser tracker
  // ------------------------------------------------------------------------------------------------------------------

  const createLogger = (debugEnabled, consoleObject) => {
    const output = consoleObject || { log() {}, info() {}, warn() {}, error() {} };
    const gated = (method) => (...messageParts) => {
      if (debugEnabled) output[method]("[SPG_DataLayer]", ...messageParts);
    };
    return { log: gated("log"), info: gated("info"), warn: gated("warn"), error: (...messageParts) => output.error("[SPG_DataLayer]", ...messageParts) };
  };

  const createMemoryStorage = () => {
    const store = new Map();
    return {
      getItem: (key) => (store.has(key) ? store.get(key) : null),
      setItem: (key, value) => store.set(key, String(value)),
      removeItem: (key) => store.delete(key),
    };
  };

  const resolveSessionStorage = (windowObject) => {
    try {
      const storage = windowObject.sessionStorage;
      storage.getItem("spg_datalayer_probe");
      return storage;
    } catch (error) {
      return createMemoryStorage();
    }
  };

  const createTracker = (config, environment) => {
    const windowObject = environment.window;
    const documentObject = environment.document;
    const storage = environment.sessionStorage || createMemoryStorage();
    const options = Object.assign({}, DEFAULT_OPTIONS, config.options || {});
    const logger = createLogger(config.debug, environment.console);
    const productRegistry = (windowObject.products_obj = windowObject.products_obj || {});
    const seenProductIds = new Set();
    const observedCards = new WeakSet();
    let impressionQueue = [];
    let impressionTimer = null;
    let mutationTimer = null;
    let intersectionObserver = null;
    let cartItems = (config.cartItems || []).map((item) => Object.assign({}, item));

    // ---- publishing ----
    const publish = (payload) => {
      if (config.hybrid !== false) {
        windowObject.dataLayer = windowObject.dataLayer || [];
        windowObject.dataLayer.push(payload);
      } else if (windowObject.Shopify && windowObject.Shopify.analytics && typeof windowObject.Shopify.analytics.publish === "function") {
        windowObject.Shopify.analytics.publish("custom_dataLayer_event", payload);
      } else {
        logger.warn("Shopify analytics API not available", payload);
        return;
      }
      logger.log("publish", payload);
    };

    const publishEcommerce = (eventName, ecommerce) => ecommerceEvents(eventName, ecommerce).forEach(publish);

    // The payload is built when the timer fires so Discount Ninja price updates applied meanwhile are included.
    const publishEcommerceLater = (eventName, buildEcommerce) =>
      windowObject.setTimeout(() => publishEcommerce(eventName, buildEcommerce()), options.waitTime);

    const trackEvent = (eventName, payload) => publish(Object.assign({ event: eventName }, payload || {}));

    // ---- session helpers ----
    const readSession = (key) => {
      try {
        const raw = storage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    };
    const writeSession = (key, value) => {
      try {
        storage.setItem(key, JSON.stringify(value));
      } catch (error) {
        /* storage blocked */
      }
    };
    const removeSession = (key) => {
      try {
        storage.removeItem(key);
      } catch (error) {
        /* storage blocked */
      }
    };
    const listNameForHandle = (handle, fallback) => readMapEntry(storage, STORAGE_KEYS.listNameMap, handle, fallback || "direct");
    const listIdForHandle = (handle, fallback) => readMapEntry(storage, STORAGE_KEYS.listIdMap, handle, fallback != null ? fallback : "");

    // ---- list name / id resolution from the DOM ----
    const resolveListName = (cardElement) => {
      const override = cardElement.closest(SELECTORS.listNameOverride);
      const holder = cardElement.closest(SELECTORS.collectionIdHolder);
      const section = cardElement.closest(SELECTORS.sectionRoot);
      const heading = section ? section.querySelector(SELECTORS.sectionHeading) || section.querySelector(SELECTORS.sectionFallbackHeading) : null;
      return listNameFromContext(
        {
          overrideName: override ? override.getAttribute("data-spg-list-name") : "",
          holderCollectionId: holder ? holder.getAttribute("data-collection-id") : null,
          headingText: heading ? heading.textContent : "",
        },
        config
      );
    };

    const resolveListId = (cardElement) => {
      const holder = cardElement.closest(SELECTORS.collectionIdHolder);
      const value = holder ? holder.getAttribute("data-collection-id") : "";
      if (value) return Number(value) || value;
      return config.collectionId != null ? config.collectionId : "";
    };

    const cardIndex = (cardElement) => {
      const slide = cardElement.closest(SELECTORS.swiperSlide);
      if (slide) return Number(slide.getAttribute("data-swiper-slide-index")) + 1;
      const scope = cardElement.closest(SELECTORS.sectionRoot) || documentObject;
      return Array.prototype.indexOf.call(scope.querySelectorAll(SELECTORS.productCard), cardElement) + 1;
    };

    // ---- product registry (window.products_obj, keyed by product id) ----
    const productFromCard = (cardElement) => {
      const productId = cardElement.getAttribute("data-product-id");
      if (!productId) return null;
      if (!productRegistry[productId]) {
        const parsed = parseProductObject(cardElement.getAttribute("data-product-object"));
        if (!parsed) return null;
        productRegistry[productId] = parsed;
      }
      return productRegistry[productId];
    };

    const registerProduct = (item) => {
      if (item && item.item_id != null) productRegistry[String(item.item_id)] = item;
      return item;
    };

    // ---- impressions (view_item_list) ----
    // The queue holds references (product id + list data); the items are built from the registry when the event is
    // pushed, so Discount Ninja price updates applied meanwhile are included (old-theme behaviour).
    const flushImpressions = () => {
      if (!impressionQueue.length) return;
      const batch = impressionQueue;
      impressionQueue = [];
      const groups = new Map();
      batch.forEach((entry) => {
        const groupKey = entry.listName + "|" + entry.listId;
        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey).push(entry);
      });
      groups.forEach((entries) => {
        publishEcommerceLater("view_item_list", () => ({
          item_list_id: entries[0].listId,
          item_list_name: entries[0].listName,
          items: entries.map((entry) =>
            Object.assign({}, productRegistry[entry.productId] || {}, { item_list_name: entry.listName, item_list_id: entry.listId, index: entry.index })
          ),
        }));
      });
    };

    const queueImpression = (cardElement) => {
      const item = productFromCard(cardElement);
      if (!item) return;
      const productId = String(item.item_id);
      if (seenProductIds.has(productId)) return;
      seenProductIds.add(productId);
      impressionQueue.push({ productId, listName: resolveListName(cardElement), listId: resolveListId(cardElement), index: cardIndex(cardElement) });
      windowObject.clearTimeout(impressionTimer);
      impressionTimer = windowObject.setTimeout(flushImpressions, options.impressionDebounce);
    };

    const onIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio < options.viewportThreshold) return;
        intersectionObserver.unobserve(entry.target);
        queueImpression(entry.target);
      });
    };

    const touchesViewport = (element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.top < windowObject.innerHeight && rect.bottom > 0 && rect.left < windowObject.innerWidth && rect.right > 0;
    };

    let initialScanDone = false;

    const observeCard = (cardElement) => {
      if (observedCards.has(cardElement)) return;
      observedCards.add(cardElement);
      // Old-theme parity: cards touching the viewport on page load count immediately; later ones need 60 % visibility.
      if (!intersectionObserver || (!initialScanDone && touchesViewport(cardElement))) {
        queueImpression(cardElement);
        return;
      }
      intersectionObserver.observe(cardElement);
    };

    const scanCards = () => {
      documentObject.querySelectorAll(SELECTORS.productCard).forEach(observeCard);
      initialScanDone = true;
    };

    const watchDom = () => {
      if (typeof MutationObserver === "undefined" || !documentObject.body) return;
      const mutationObserver = new MutationObserver(() => {
        windowObject.clearTimeout(mutationTimer);
        mutationTimer = windowObject.setTimeout(scanCards, options.mutationDebounce);
      });
      mutationObserver.observe(documentObject.body, { childList: true, subtree: true });
    };

    // ---- card click → list breadcrumb for select_item / item_list_name on the next pages ----
    const productHandleFromHref = (href) => {
      const match = String(href || "").split("?")[0].split("#")[0].match(/\/products\/([^/]+)\/?$/);
      return match ? decodeURIComponent(match[1]) : null;
    };

    const handleProductCardClick = (event) => {
      const cardElement = event.target.closest(SELECTORS.productCard);
      if (!cardElement) return;
      const anchor = event.target.closest("a[href]") || cardElement.querySelector(SELECTORS.productLink);
      const handle = anchor ? productHandleFromHref(anchor.getAttribute("href")) : null;
      if (!handle) return;
      const listName = resolveListName(cardElement);
      const listId = resolveListId(cardElement);
      writeSession(STORAGE_KEYS.selectedProduct, { handle, item_list_id: listId, item_list_name: listName, index: cardIndex(cardElement) });
      writeSession(STORAGE_KEYS.productBreadcrumb, { handle, item_list_id: listId, item_list_name: listName });
      writeMapEntry(storage, STORAGE_KEYS.listNameMap, handle, listName);
      writeMapEntry(storage, STORAGE_KEYS.listIdMap, handle, listId);
      logger.log("product card click", handle, listName, listId);
    };

    // ---- page triggers ----
    const trackProductPage = () => {
      const product = config.productObject;
      if (!product) return;
      const breadcrumb = readSession(STORAGE_KEYS.productBreadcrumb);
      const cameFromList = breadcrumb && breadcrumb.handle === product.handle;
      product.item_list_name = cameFromList ? breadcrumb.item_list_name : config.itemListName || "direct";
      product.item_list_id = cameFromList ? breadcrumb.item_list_id : "";
      registerProduct(product);

      const selected = readSession(STORAGE_KEYS.selectedProduct);
      if (selected && selected.handle === product.handle) {
        publishEcommerceLater("select_item", () => {
          removeSession(STORAGE_KEYS.selectedProduct);
          return {
            item_list_id: selected.item_list_id,
            item_list_name: selected.item_list_name,
            items: [Object.assign({}, product, { item_list_name: selected.item_list_name, item_list_id: selected.item_list_id, index: selected.index, quantity: 1 })],
          };
        });
      }
      publishEcommerceLater("view_item", () => ({ currency: config.currency, value: product.price, items: [product] }));
    };

    const trackSearchPage = () => {
      if (config.templateName !== "search") return;
      const cards = Array.prototype.slice.call(documentObject.querySelectorAll(SELECTORS.productCard), 0, options.searchResultsCount);
      const items = cards.map(productFromCard).filter(Boolean).map((item, position) => Object.assign({}, item, { index: position + 1 }));
      publishEcommerce("view_search_result", { items, search_string: config.searchTerms || "" });
    };

    // ---- cart (view_cart from the Liquid snapshot; the live cart handlers extend this in the cart module) ----
    const applyListDataToCartItems = () => {
      cartItems.forEach((item) => {
        item.item_list_name = listNameForHandle(item.handle, item.item_list_name);
        item.item_list_id = listIdForHandle(item.handle, item.item_list_id);
      });
    };

    const saveBundleMap = () => {
      const bundleMap = {};
      cartItems.forEach((item) => {
        if (Array.isArray(item.item_components) && item.item_components.length) bundleMap[item.item_id] = Object.assign({}, item);
      });
      writeSession(STORAGE_KEYS.bundleMap, bundleMap);
    };

    const publishViewCart = (discountedCart, fallbackValue) => {
      applyListDataToCartItems();
      saveBundleMap();
      publishEcommerce("view_cart", viewCartPayload({ items: cartItems, discountedCart, currency: config.currency, fallbackValue }));
    };

    // Cart page: the Liquid prices already include Discount Ninja's cart discount (DN reloads the page after changes).
    const trackCartPage = () => {
      if (config.templateName === "cart") publishViewCart(null, config.cartTotal);
    };

    // ---- Discount Ninja price sync ----
    let discountNinjaReady = false;

    const discountNinjaApi = () => (windowObject.discountNinja && windowObject.discountNinja.api) || null;

    const discountedCartItems = () => {
      try {
        const api = discountNinjaApi();
        const content = discountNinjaReady && api && api.discountedCart ? api.discountedCart.content : null;
        return content && Array.isArray(content.items) ? content.items : [];
      } catch (error) {
        return [];
      }
    };

    const applyProductDiscount = (event) => {
      const data = event && event.detail && Array.isArray(event.detail.data) ? event.detail.data[0] : null;
      if (!data || data.isDiscountedByOffer !== true) return;
      const price = data.discountedPrice != null ? toMoney(data.discountedPrice) : null;
      const discount = data.amount != null ? toMoney(data.amount) : null;
      const applyTo = (item) => {
        if (price != null) item.price = price;
        if (discount != null) item.discount = discount;
      };
      const product = config.productObject;
      if (product && (data.isCurrentProduct === true || data.productHandle === product.handle)) applyTo(product);
      Object.keys(productRegistry).forEach((key) => {
        const entry = productRegistry[key];
        if (entry && String(entry.variantId) === String(data.variantId)) applyTo(entry);
      });
      logger.log("discount ninja product discount applied", data);
    };

    const handleDiscountedCartUpdate = (event) => {
      const discountedCart = event && event.detail && Array.isArray(event.detail.data) ? event.detail.data[0] : null;
      if (config.templateName !== "cart" || !discountedCart) return;
      writeSession(STORAGE_KEYS.bundleMap, updateBundleMap(readSession(STORAGE_KEYS.bundleMap) || {}, discountedCart));
    };

    const setupDiscountNinja = () => {
      const activate = () => {
        discountNinjaReady = true;
        documentObject.addEventListener("la:dn:product:discount:calculated", applyProductDiscount);
        documentObject.addEventListener("la:dn:cart:updated", handleDiscountedCartUpdate);
        logger.log("discount ninja api ready");
      };
      if (discountNinjaApi()) {
        activate();
      } else {
        documentObject.addEventListener("la:dn:api:ready", activate, { once: true });
      }
    };

    // ---- cart events (add_to_cart / remove_from_cart / view_cart) ----
    const cartBuildOptions = () => ({
      shopUrl: config.shopUrl,
      currency: config.currency,
      resolveListName: (handle) => listNameForHandle(handle),
      resolveListId: (handle) => listIdForHandle(handle),
    });

    const knownItemsByVariant = () =>
      cartItems.reduce((map, item) => {
        map[item.variantId] = item;
        return map;
      }, {});

    const refreshCartSnapshot = (cartJson) => {
      if (cartJson && Array.isArray(cartJson.items)) cartItems = buildCartItems(cartJson, cartBuildOptions());
      return cartJson;
    };

    const routeUrl = (path) => (config.routesRoot || "/").replace(/\/?$/, "/") + path;

    const fetchJson = (url) =>
      windowObject.fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } }).then((response) => {
        if (!response.ok) throw new Error("request failed: " + response.status);
        return response.json();
      });

    const fetchCart = () => fetchJson(routeUrl("cart.js")).then(refreshCartSnapshot);

    const fetchProduct = (handle) => fetchJson(routeUrl("products/" + encodeURIComponent(handle) + ".js"));

    // Delayed cart events stay registered until they are pushed: Discount Ninja can reload the page right after a
    // drawer quantity change, so a pagehide flushes them instead of losing them inside the wait time.
    const pendingCartEvents = new Set();

    const flushPendingCartEvents = () => {
      pendingCartEvents.forEach((pending) => {
        windowObject.clearTimeout(pending.timer);
        publishEcommerce(pending.eventName, pending.buildEcommerce());
      });
      pendingCartEvents.clear();
    };

    const publishCartEvent = (eventName, items, immediate) => {
      const buildEcommerce = () => {
        const syncedItems = mergeDiscountNinjaPrices(items, discountedCartItems());
        return { currency: config.currency, value: sumItemsValue(syncedItems), items: syncedItems };
      };
      if (immediate) {
        publishEcommerce(eventName, buildEcommerce());
        return;
      }
      const pending = { eventName, buildEcommerce, timer: null };
      pending.timer = windowObject.setTimeout(() => {
        pendingCartEvents.delete(pending);
        publishEcommerce(eventName, buildEcommerce());
      }, options.waitTime);
      pendingCartEvents.add(pending);
    };

    const handleCartAdd = (responseJson, requestBody) => {
      addedLineItems(responseJson).forEach((line) => {
        const quantity = addedQuantity(requestBody, line);
        const registeredItem = productRegistry[String(line.product_id)];
        const listName = listNameForHandle(line.handle, registeredItem ? registeredItem.item_list_name : undefined);
        const listId = listIdForHandle(line.handle, registeredItem ? registeredItem.item_list_id : undefined);
        const withLineData = (item) =>
          Object.assign({}, item, {
            quantity,
            variantId: line.variant_id,
            variantTitle: line.variant_title || item.variantTitle,
            variant: line.image || item.variant,
            sku: line.sku || item.sku,
            item_list_name: listName,
            item_list_id: listId,
          });
        if (registeredItem) {
          publishCartEvent("add_to_cart", [withLineData(registeredItem)]);
          return;
        }
        fetchProduct(line.handle)
          .then((productJson) =>
            registerProduct(formatProductData(productJson, { variantId: line.variant_id, currency: config.currency, shopUrl: config.shopUrl, listName, listId }))
          )
          .catch(() => buildCartItems({ items: [line], currency: config.currency }, cartBuildOptions())[0])
          .then((item) => publishCartEvent("add_to_cart", [withLineData(item)]));
      });
    };

    const recentPageChanges = [];

    const rememberPageChange = (action, item) => {
      recentPageChanges.push({ action, variantId: String(item.variantId), quantity: item.quantity, time: Date.now() });
    };

    // A cart-page quantity link was already pushed synchronously; skip the same change if an app replays it over AJAX.
    const withoutRecentPageChanges = (action, items) =>
      items.filter((item) => {
        const now = Date.now();
        const duplicateIndex = recentPageChanges.findIndex(
          (entry) => entry.action === action && entry.variantId === String(item.variantId) && entry.quantity === item.quantity && now - entry.time < PAGE_CHANGE_DEDUPE_WINDOW
        );
        if (duplicateIndex === -1) return true;
        recentPageChanges.splice(duplicateIndex, 1);
        return false;
      });

    const handleCartChange = (responseJson) => {
      if (!responseJson || typeof responseJson !== "object") return;
      const known = knownItemsByVariant();
      let added = computeCartChange(responseJson.items_added, known, cartBuildOptions()).items;
      let removed = computeCartChange(responseJson.items_removed, known, cartBuildOptions()).items;
      if (!added.length && !removed.length && Array.isArray(responseJson.items)) {
        // Apps can re-issue the change and hide the original response (Discount Ninja does in the drawer): the
        // response then has no items_added / items_removed, so the difference against the snapshot is used.
        const delta = cartDelta(cartItems, responseJson, cartBuildOptions());
        added = delta.added;
        removed = delta.removed;
      }
      added = withoutRecentPageChanges("add", added);
      removed = withoutRecentPageChanges("remove", removed);
      refreshCartSnapshot(responseJson);
      if (added.length) publishCartEvent("add_to_cart", added);
      if (removed.length) publishCartEvent("remove_from_cart", removed);
    };

    const dispatchCartRequest = (requestType, responseJson, requestBody) => {
      const eventName = CART_EVENTS[requestType];
      if (!eventName) return;
      logger.log("cart request", requestType, responseJson, requestBody);
      windowObject.dispatchEvent(new CustomEvent(eventName, { detail: { response: responseJson, request: requestBody } }));
    };

    const listenCartRequests = () => {
      windowObject.addEventListener(CART_EVENTS.add, (event) => handleCartAdd(event.detail.response, event.detail.request));
      windowObject.addEventListener(CART_EVENTS.change, (event) => handleCartChange(event.detail.response));
      windowObject.addEventListener(CART_EVENTS.update, (event) => handleCartChange(event.detail.response));
      [CART_EVENTS.get, CART_EVENTS.clear].forEach((eventName) => {
        windowObject.addEventListener(eventName, (event) => refreshCartSnapshot(event.detail.response));
      });
    };

    const requestUrlOf = (input) => {
      if (typeof input === "string") return input;
      if (input && typeof input.url === "string") return input.url;
      return input != null ? String(input) : "";
    };

    // Wrap fetch / XMLHttpRequest so cart mutations made by the theme AND by apps are detected (old-theme mechanism).
    // The wrappers are classic `function`s on purpose: they must forward the caller's `this` and `arguments`
    // (and the XHR ones run as prototype methods), which arrow functions cannot do.
    const installRequestInterception = () => {
      if (typeof windowObject.fetch === "function" && !windowObject.fetch.__spgDataLayerWrapped) {
        const originalFetch = windowObject.fetch;
        const wrappedFetch = function (input, requestOptions) {
          const requestType = cartRequestType(requestUrlOf(input));
          const requestBody = requestType ? parseRequestBody(requestOptions && requestOptions.body, requestOptions && requestOptions.headers) : null;
          const responsePromise = originalFetch.apply(this, arguments);
          if (requestType) {
            responsePromise
              .then((response) => {
                if (!response || !response.ok) return;
                response
                  .clone()
                  .json()
                  .then((data) => dispatchCartRequest(requestType, data, requestBody))
                  .catch(() => {});
              })
              .catch(() => {});
          }
          return responsePromise;
        };
        wrappedFetch.__spgDataLayerWrapped = true;
        windowObject.fetch = wrappedFetch;
      }
      const xhrPrototype = windowObject.XMLHttpRequest && windowObject.XMLHttpRequest.prototype;
      if (xhrPrototype && !xhrPrototype.__spgDataLayerWrapped) {
        const originalOpen = xhrPrototype.open;
        const originalSend = xhrPrototype.send;
        xhrPrototype.open = function (method, url) {
          this.__spgRequestType = cartRequestType(requestUrlOf(url));
          return originalOpen.apply(this, arguments);
        };
        xhrPrototype.send = function (body) {
          if (this.__spgRequestType) {
            const requestType = this.__spgRequestType;
            const requestBody = parseRequestBody(body);
            this.addEventListener("load", () => {
              if (this.status < 200 || this.status >= 300) return;
              try {
                const payload = this.responseType === "json" ? this.response : JSON.parse(this.responseText);
                dispatchCartRequest(requestType, payload, requestBody);
              } catch (error) {
                /* not a JSON response */
              }
            });
          }
          return originalSend.apply(this, arguments);
        };
        xhrPrototype.__spgDataLayerWrapped = true;
      }
    };

    // Discount Ninja re-evaluates the cart after every change and announces it with la:dn:cart:updated. The drawer's
    // view_cart waits for that (bounded by options.discountNinjaWait) unless DN's discounted cart already matches the
    // Shopify cart, so the pushed prices are the ones the visitor sees in the drawer.
    const waitForDiscountedCart = (cartJson) =>
      new Promise((resolve) => {
        const api = discountNinjaApi();
        if (!api) {
          resolve(null);
          return;
        }
        const currentContent = api.discountedCart && api.discountedCart.content;
        if (discountedCartMatchesCart(currentContent, cartJson)) {
          resolve(currentContent);
          return;
        }
        let settled = false;
        let timer = null;
        const finish = (discountedCart) => {
          if (settled) return;
          settled = true;
          windowObject.clearTimeout(timer);
          documentObject.removeEventListener("la:dn:cart:updated", onCartUpdated);
          resolve(discountedCart);
        };
        const onCartUpdated = (event) => finish(discountedCartFromEvent(event));
        documentObject.addEventListener("la:dn:cart:updated", onCartUpdated);
        timer = windowObject.setTimeout(() => {
          logger.warn("discount ninja cart not updated in time, using the available data");
          finish((api.discountedCart && api.discountedCart.content) || null);
        }, options.discountNinjaWait);
      });

    const listenCartDrawer = () => {
      const drawer = documentObject.querySelector(SELECTORS.cartDrawer);
      if (!drawer) return;
      drawer.addEventListener("dialog:after-show", () => {
        fetchCart()
          .then((cartJson) => waitForDiscountedCart(cartJson).then((discountedCart) => publishViewCart(discountedCart, toMoney(cartJson.total_price))))
          .catch((error) => logger.error("cart fetch failed", error));
      });
    };

    const listenCartPageChanges = () => {
      if (config.templateName !== "cart") return;
      const publishPageChange = (lineKey, targetQuantity) => {
        const change = cartPageChange(lineKey, targetQuantity, cartItems);
        if (!change) return;
        change.items.forEach((item) => rememberPageChange(change.action, item));
        publishCartEvent(change.action === "add" ? "add_to_cart" : "remove_from_cart", change.items, true);
      };
      documentObject.addEventListener(
        "click",
        (event) => {
          const link = event.target.closest(SELECTORS.cartChangeLink);
          const parsed = link ? parseCartChangeLink(link.getAttribute("href")) : null;
          if (parsed) publishPageChange(parsed.lineKey != null ? parsed.lineKey : parsed.line, parsed.quantity);
        },
        true
      );
      documentObject.addEventListener(
        "change",
        (event) => {
          const input = event.target;
          if (!input || typeof input.matches !== "function" || !input.matches(SELECTORS.cartQuantityInput)) return;
          publishPageChange(input.getAttribute("data-line-key"), Number(input.value));
        },
        true
      );
    };

    // ---- sort / filter (product_sort, product_filter) ----
    const labelTextForInput = (input) => {
      const label =
        (input.labels && input.labels[0]) ||
        (input.id ? documentObject.querySelector("label[for=\"" + input.id + "\"]") : null) ||
        input.closest("label");
      if (label) return normalizeListName(label.textContent);
      return normalizeListName(input.name + "=" + input.value);
    };

    // Capture phase: the theme's own listeners navigate (sort select, tag filters), ours must run first.
    const listenSortAndFilter = () => {
      documentObject.addEventListener(
        "change",
        (event) => {
          const target = event.target;
          if (!target || typeof target.matches !== "function") return;
          if (target.matches(SELECTORS.sortSelect)) {
            const option = target.options ? target.options[target.selectedIndex] : null;
            trackEvent("product_sort", { sort_option: normalizeListName(option ? option.textContent : target.value) });
          } else if (target.matches(SELECTORS.facetInput)) {
            trackEvent("product_filter", { filter_option: labelTextForInput(target) });
          }
        },
        true
      );
      documentObject.addEventListener(
        "click",
        (event) => {
          const filterItem = event.target.closest(SELECTORS.tagFilterItem);
          if (filterItem) trackEvent("product_filter", { filter_option: normalizeListName(filterItem.textContent) });
        },
        true
      );
    };

    // ---- newsletter (Klaviyo embedded forms dispatch "klaviyoForms" on window) ----
    const listenNewsletter = () => {
      windowObject.addEventListener("klaviyoForms", (event) => {
        const detail = event && event.detail;
        if (!detail || detail.type !== "submit") return;
        trackEvent("newsletter_subscribe", { form_id: detail.formId || "" });
      });
    };

    const init = () => {
      if (config.enabled === false) return;
      installRequestInterception();
      listenCartRequests();
      windowObject.addEventListener("pagehide", flushPendingCartEvents);
      if (typeof IntersectionObserver !== "undefined") {
        intersectionObserver = new IntersectionObserver(onIntersection, { threshold: [options.viewportThreshold] });
      }
      documentObject.addEventListener("click", handleProductCardClick);
      scanCards();
      watchDom();
      setupDiscountNinja();
      listenCartDrawer();
      listenCartPageChanges();
      listenSortAndFilter();
      listenNewsletter();
      trackProductPage();
      trackSearchPage();
      trackCartPage();
      logger.log("ready", config);
    };

    return {
      init,
      publish,
      publishEcommerce,
      trackEvent,
      resolveListName,
      resolveListId,
      productFromCard,
      getCartItems: () => cartItems,
      setCartItems: (items) => {
        cartItems = items;
      },
      publishViewCart,
      publishCartEvent,
      handleCartChange,
      flushPendingCartEvents,
      waitForDiscountedCart,
      logger,
      options,
      storage,
    };
  };

  if (typeof module !== "undefined" && module.exports) {
    module.exports = { helpers, createTracker, DEFAULT_OPTIONS, STORAGE_KEYS, SELECTORS };
  }

  const windowObject = typeof window !== "undefined" ? window : null;
  if (windowObject && windowObject.document && windowObject.SPG_DataLayerConfig) {
    const start = () => {
      windowObject.SPG_DataLayer = createTracker(windowObject.SPG_DataLayerConfig, {
        window: windowObject,
        document: windowObject.document,
        sessionStorage: resolveSessionStorage(windowObject),
        console: windowObject.console,
      });
      windowObject.SPG_DataLayer.init();
    };
    if (windowObject.document.readyState === "loading") {
      windowObject.document.addEventListener("DOMContentLoaded", start);
    } else {
      start();
    }
  }
})(typeof globalThis !== "undefined" ? globalThis : this);
